import { connect } from 'react-redux';
import type { MutableRefObject } from 'react';
import { useRef } from 'react';
import { breakpoints, Card, Column, Columns, Heading, PageBlock, Stack } from '@etg/wings';
import { ConditionalWrapper } from '@eti/components';
import { css } from '@eti/styles';
import { ProductCardLayoutType, simpleVisa, useSeatMapModal } from '@eti/products';
import { analytics } from '@eti/utils';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { useHistory } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import type { FormStateMap } from 'redux-form';
import { useMediaQuery } from 'usehooks-ts';
import { routes } from '../../../../../constants/routesAndApisConstants';
import OrderInfo from '../../../../../widgets/order-info/text-resolvers/OrderInfoTextResolver';
import useHashScroll from '../../custom-hooks/hashScroll';
import { getAddedExtraProducts, isSeatingOffered } from '../../../../../utils/addedExtraProducts';
import type { OrderTypePostbooking } from '../../utils/OrderUtils';
import { getAvailableExtraProducts } from '../../utils/OrderUtils';
import { pushViewItemToDataLayer } from '../../../../../common/data-layer/ecommerce/pushViewItemToDataLayer';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import TrustFacts from './TrustFacts';
import FinePrint from './FinePrint';
import PayButton from './PayButton';

const productWrapperStyles = css`
  .buttonWrapper {
    @media (max-width: ${breakpoints._768}) {
      margin-top: 28px;
    }
  }

  .priceLabel {
    @media (min-width: ${breakpoints._768}) {
      /* stylelint-disable-next-line declaration-no-important */
      margin-top: 8px !important;
    }
  }
`;

const payButtonWrapperStyle = css`
  text-align: center;
`;

const orderInfoStyles = css`
  @media (min-width: ${breakpoints._970}) {
    margin-bottom: 80px;
  }
`;

type AddedExtraProductsType = {
  id: number;
  markup: number | null;
  name: string;
  price: number;
  travelers: { label: string; price: string; priceRaw: number; travelerId: string }[] | [];
  title?: string;
  numberOfUnits?: number;
}[];

export type ExtraProductsFormTypes = {
  addedExtraProducts?: AddedExtraProductsType;
  contactEmail: string | null;
  form: string;
  isLoading: boolean;
  onSubmit: () => void;
  order: OrderTypePostbooking;
  tabHeading1Ref?: MutableRefObject<HTMLHeadingElement | null>;
};

const ExtraProductsForm = ({
  addedExtraProducts,
  contactEmail,
  form,
  isLoading,
  onSubmit,
  order,
  tabHeading1Ref,
}: ExtraProductsFormTypes) => {
  const { brand } = useSiteContext();
  const { p } = useProperty();
  const { t } = useTranslation();
  const cartRef = useRef(null);
  const history = useHistory();
  const { isSeatMapModalOpen, setIsSeatMapModalOpen } = useSeatMapModal();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._970})`);

  const isOrderDetailsPage = history.location.pathname.includes(routes.ORDER_DETAILS);
  useHashScroll(isOrderDetailsPage);

  const products = getAvailableExtraProducts({ order });
  analytics.useGADataLayerEffect({
    event: 'seatingVisible',
    seatingOffered: isSeatingOffered(products) ? 'YES' : 'NO',
  });

  const shouldShowImage = p('TravelerDetails.ProductImages.Enabled');

  return (
    <ConditionalWrapper
      condition={!isOrderDetailsPage}
      wrapper={(children) => <PageBlock withPaddingY>{children}</PageBlock>}
    >
      <form aria-labelledby="extraProductsForm-heading" id="extraProductsForm" onSubmit={onSubmit}>
        <Stack>
          <Heading
            as={isOrderDetailsPage ? 'h1' : 'h2'}
            className={isOrderDetailsPage ? screenReadersOnlyStyles : ''}
            id="extraProductsForm-heading"
            level={2}
            ref={isOrderDetailsPage ? tabHeading1Ref : undefined}
            tabIndex={isOrderDetailsPage ? -1 : undefined}
          >
            {t('Postbooking.Persuade.Header')}
          </Heading>
          <Columns>
            <Column width={{ [breakpoints._0]: '4/4', [breakpoints._970]: '4/6' }}>
              {products?.length ? (
                <Stack as="ul" spacing={24}>
                  {products.map((product) => {
                    const ExtraProductComponent = product.component?.type;
                    return product.component ? (
                      <InView
                        as="li"
                        className={productWrapperStyles}
                        id={`product-id-${product.id}`}
                        key={product.id}
                        onChange={(inView) =>
                          inView &&
                          pushViewItemToDataLayer(
                            product.code,
                            product.id,
                            product.component.props.options,
                          )
                        }
                        threshold={1}
                        triggerOnce
                      >
                        <ExtraProductComponent
                          brand={brand.code}
                          confirmation={product.confirmation}
                          form={form}
                          id={product.id}
                          isMultiStop={order.isMultiStop}
                          isPostBooking
                          isSeatMapModalOpen={isSeatMapModalOpen}
                          order={order}
                          productSpecificTexts={product.productSpecificTexts}
                          sellSpecification={product.sellSpecification}
                          setIsSeatMapModalOpen={setIsSeatMapModalOpen}
                          shouldShowImage={shouldShowImage}
                          travelerLabel={t('TravelerDescription.Header')}
                          {...(product.id === simpleVisa.id ? { contactEmail } : {})}
                          layoutType={ProductCardLayoutType.PostBooking}
                        />
                      </InView>
                    ) : null;
                  })}

                  {!isWiderViewport && (
                    <div className={payButtonWrapperStyle}>
                      {addedExtraProducts && addedExtraProducts.length > 0 ? (
                        <PayButton isLoading={isLoading} />
                      ) : (
                        <span>{t('Postbooking.ProductPage.NoProductsInCartMessage')}</span>
                      )}
                    </div>
                  )}

                  <TrustFacts />
                  <FinePrint />
                </Stack>
              ) : (
                <section data-testid="no-available-products-message">
                  <Card>
                    <Stack>
                      <Heading level={2}>{t('Postbooking.NoAvailableProducts.Header')}</Heading>
                      <p>{t('Postbooking.NoAvailableProducts.Text')}</p>
                    </Stack>
                  </Card>
                </section>
              )}
            </Column>
            <Column className={orderInfoStyles} ref={cartRef} width="2/6">
              <OrderInfo cartRef={cartRef} form={form} isLoading={isLoading} order={order} />
            </Column>
          </Columns>
        </Stack>
      </form>
    </ConditionalWrapper>
  );
};

const mapStateToProps = (
  state: FormStateMap,
  { form, names, ...rest }: { names?: String[] } & ExtraProductsFormTypes,
) => ({
  addedExtraProducts: getAddedExtraProducts(form, names)(state) || [],
  ...rest,
});

export default connect(mapStateToProps)(ExtraProductsForm);
